import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { ThreatsTab } from '../features/security/components/ThreatsTab';
import { SourcesTab } from '../features/security/components/SourcesTab';
import { ForwardWiringStatusTab } from '../features/security/components/ForwardWiringStatusTab';
import { ThreatDetailPanel } from '../features/security/components/ThreatDetailPanel';

type SecurityTab = 'threats' | 'sources' | 'forward-wiring';

const TABS: { id: SecurityTab; label: string }[] = [
  { id: 'threats', label: 'Threats' },
  { id: 'sources', label: 'Sources' },
  { id: 'forward-wiring', label: 'Forward-Wiring Status' },
];

function isSecurityTab(value: string | null): value is SecurityTab {
  return value === 'threats' || value === 'sources' || value === 'forward-wiring';
}

const SecurityPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: SecurityTab = isSecurityTab(tabParam) ? tabParam : 'threats';

  const setActiveTab = (tab: SecurityTab) => {
    setSearchParams((prev) => {
      prev.set('tab', tab);
      // Close the detail panel when switching tabs
      prev.delete('canonical_event_id');
      return prev;
    });
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <header style={{ marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>Security</h2>
        <p
          style={{
            color: 'var(--text-secondary)',
            marginTop: '0.25rem',
            fontSize: '0.875rem',
          }}
        >
          Multi-skill-aware platform security dashboard. Today: cyber-agent
          (CVE intel correlation + forward-wiring). Phase 6.x security skills
          plug into this surface.
        </p>
      </header>
      <div
        role="tablist"
        aria-label="Security dashboard tabs"
        style={{
          display: 'flex',
          gap: '0.25rem',
          borderBottom: '1px solid var(--border-default, #e5e7eb)',
          marginBottom: '1rem',
        }}
      >
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            role="tab"
            aria-selected={activeTab === id}
            aria-controls={`security-tabpanel-${id}`}
            id={`security-tab-${id}`}
            onClick={() => setActiveTab(id)}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              borderBottom: activeTab === id
                ? '2px solid var(--accent-primary, #3b82f6)'
                : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              fontWeight: activeTab === id ? 600 : 400,
              color:
                activeTab === id
                  ? 'var(--text-primary)'
                  : 'var(--text-secondary)',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div
        role="tabpanel"
        id={`security-tabpanel-${activeTab}`}
        aria-labelledby={`security-tab-${activeTab}`}
      >
        {activeTab === 'threats' && <ThreatsTab />}
        {activeTab === 'sources' && <SourcesTab />}
        {activeTab === 'forward-wiring' && <ForwardWiringStatusTab />}
      </div>

      <ThreatDetailPanel />
    </div>
  );
};

export default SecurityPage;
